import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import apiClient from '../services/api';
import './admin/AdminLayout.css';
import ConfirmDialog from '../components/ConfirmDialog';
import { Report, ReportTokenStats, formatTokenCount, formatCostUSD, getStatusClass, getStatusText } from '../types/admin';

interface UserInfo {
  id: number;
  name: string;
  email: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1_048_576) return (bytes / 1_048_576).toFixed(1) + ' МБ';
  if (bytes >= 1024) return (bytes / 1024).toFixed(0) + ' КБ';
  return bytes + ' Б';
};

const UserReportsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false);
  const [reportToDelete, setReportToDelete] = useState<number | null>(null);

  // Пагинация
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  // Token statistics
  const [reportTokenStats, setReportTokenStats] = useState<ReportTokenStats[]>([]);
  const [expandedTokenReportId, setExpandedTokenReportId] = useState<number | null>(null);

  // JSON viewer
  const [jsonData, setJsonData] = useState<any>(null);
  const [jsonReportId, setJsonReportId] = useState<number | null>(null);
  const [jsonLoading, setJsonLoading] = useState<boolean>(false);

  // Regeneration
  const [regeneratingReportId, setRegeneratingReportId] = useState<number | null>(null);

  // Notification modal
  const [notifyModalReportId, setNotifyModalReportId] = useState<number | null>(null);
  const [notifyTestEmail, setNotifyTestEmail] = useState<string>('');
  const [notifyTargetUserId, setNotifyTargetUserId] = useState<string>('');
  const [notifyLoading, setNotifyLoading] = useState<boolean>(false);

  // Проверка авторизации и прав администратора
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isAdmin = localStorage.getItem('isAdmin');

    if (!token) {
      navigate('/login');
    } else if (isAdmin !== 'true') {
      navigate('/');
    }
  }, [navigate]);

  const fetchData = async () => {
    try {
      setIsLoading(true);

      const reportsResponse = await apiClient.get(`/admin/users/${userId}/reports`);
      const items = reportsResponse.data;
      setReports(items);

      // Extract token stats from enriched data
      const tokenStats: ReportTokenStats[] = items
        .filter((r: any) => r.token_usage?.has_token_data)
        .map((r: any) => ({
          report_id: r.id,
          report_name: r.name,
          input_tokens: r.token_usage.input_tokens,
          output_tokens: r.token_usage.output_tokens,
          total_tokens: r.token_usage.total_tokens,
          provider: r.token_usage.provider || '',
          has_token_data: true,
          cost_usd: r.token_usage.cost_usd || 0,
          breakdown: r.token_usage.breakdown || { analytics: { input: 0, output: 0, total: 0, provider: '' }, reports: [], summary: { input: 0, output: 0, total: 0, provider: '' } },
        }));
      setReportTokenStats(tokenStats);

      // Загрузка информации о пользователе
      const usersResponse = await apiClient.get('/admin/users');
      const usersData = usersResponse.data.items || usersResponse.data;
      const userInfo = usersData.find((u: any) => u.id === parseInt(userId || '0'));
      if (userInfo) {
        setUser(userInfo);
      }

      setIsLoading(false);
    } catch (err) {
      console.error('Ошибка при загрузке данных:', err);
      setError('Ошибка при загрузке данных. Проверьте консоль для деталей.');
      setIsLoading(false);

      if (axios.isAxiosError(err) && err.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  useEffect(() => {
    if (userId) {
      fetchData();
    }
  }, [navigate, userId]);

  const openDeleteConfirmDialog = (reportId: number) => {
    setReportToDelete(reportId);
    setIsConfirmDialogOpen(true);
  };

  const closeDeleteConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
    setReportToDelete(null);
  };

  const handleDeleteReport = async () => {
    if (reportToDelete === null) return;
    try {
      await apiClient.delete(`/admin/reports/${reportToDelete}`);
      closeDeleteConfirmDialog();
      fetchData();
    } catch (err) {
      console.error('Ошибка при удалении отчета:', err);
      setError('Ошибка при удалении отчета');
      closeDeleteConfirmDialog();
    }
  };

  const handleViewJson = async (reportId: number) => {
    setJsonLoading(true);
    setJsonReportId(reportId);
    try {
      const response = await apiClient.get(`/admin/reports/${reportId}/json`);
      setJsonData(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке JSON:', err);
      setError('Ошибка при загрузке JSON отчета');
      setJsonReportId(null);
    } finally {
      setJsonLoading(false);
    }
  };

  const handleDownloadFile = async (reportId: number) => {
    try {
      const response = await apiClient.get(`/admin/reports/${reportId}/file`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : `report_${reportId}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Ошибка при скачивании файла:', err);
      setError('Ошибка при скачивании файла');
    }
  };

  // --- Regeneration ---
  const handleRegenerate = async (reportId: number) => {
    if (!window.confirm('Перегенерировать отчёт? Тики не будут списаны с пользователя.')) return;

    setRegeneratingReportId(reportId);
    setError(null);
    try {
      const response = await apiClient.post(`/admin/reports/${reportId}/regenerate`);
      alert(response.data.message || 'Перегенерация запущена');
      fetchData();
    } catch (err: any) {
      console.error('Ошибка при перегенерации:', err);
      const detail = err.response?.data?.detail || 'Неизвестная ошибка';
      setError(`Ошибка перегенерации: ${detail}`);
    } finally {
      setRegeneratingReportId(null);
    }
  };

  // --- Notification ---
  const handleOpenNotifyModal = (reportId: number) => {
    setNotifyModalReportId(reportId);
    setNotifyTestEmail('');
    setNotifyTargetUserId('');
    setError(null);
  };

  const handleSendNotification = async (mode: 'owner' | 'target' | 'test') => {
    if (!notifyModalReportId) return;

    setNotifyLoading(true);
    setError(null);
    try {
      const payload: any = {};
      if (mode === 'test' && notifyTestEmail) {
        payload.test_email = notifyTestEmail;
      } else if (mode === 'target' && notifyTargetUserId) {
        payload.target_user_id = parseInt(notifyTargetUserId, 10);
      }

      const response = await apiClient.post(
        `/admin/reports/${notifyModalReportId}/notify`,
        payload
      );
      alert(response.data.message || 'Email отправлен');
      setNotifyModalReportId(null);
    } catch (err: any) {
      console.error('Ошибка при отправке уведомления:', err);
      const detail = err.response?.data?.detail || 'Неизвестная ошибка';
      setError(`Ошибка отправки: ${detail}`);
    } finally {
      setNotifyLoading(false);
    }
  };

  const notifyReport = notifyModalReportId
    ? reports.find(r => r.id === notifyModalReportId)
    : null;

  if (isLoading) {
    return <div className="loading">Загрузка данных...</div>;
  }

  const paginatedReports = reports.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <>
      <div className="admin-page">
        <h1>Отчеты пользователя</h1>

        {error && <div className="error-message">{error}</div>}

        <div className="admin-card">
          <div className="user-info">
            <h2>{user ? `${user.name} (${user.email})` : `Пользователь #${userId}`}</h2>
            <button
              className="action-button back"
              onClick={() => navigate('/admin')}
            >
              Назад к списку пользователей
            </button>
          </div>

          <div className="reports-table-container">
            {reports.length > 0 ? (
              <>
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Название</th>
                      <th>Тип</th>
                      <th>Файл</th>
                      <th>Тики</th>
                      <th>Дата</th>
                      <th>Статус</th>
                      <th>Отзыв</th>
                      <th>Токены</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedReports.map(report => (
                      <tr key={report.id}>
                        <td>{report.id}</td>
                        <td>
                          {report.name}
                          {report.is_pro && (
                            <span style={{
                              display: 'inline-block',
                              marginLeft: '6px',
                              padding: '1px 6px',
                              fontSize: '10px',
                              fontWeight: 700,
                              borderRadius: '4px',
                              background: 'linear-gradient(135deg, #667eea, #764ba2)',
                              color: '#fff',
                              verticalAlign: 'middle',
                            }}>PRO</span>
                          )}
                        </td>
                        <td>{report.report_type}</td>
                        <td>
                          {report.file_exists && report.file_size != null ? (
                            <span
                              onClick={() => handleDownloadFile(report.id)}
                              style={{ cursor: 'pointer', color: '#1976d2', borderBottom: '1px dashed #1976d2' }}
                              title="Скачать исходный файл"
                            >
                              <i className="fas fa-file-excel" style={{ marginRight: '4px', color: '#217346' }}></i>
                              {formatFileSize(report.file_size)}
                            </span>
                          ) : (
                            <span style={{ color: '#999' }}>—</span>
                          )}
                        </td>
                        <td>
                          <i className="fas fa-bolt"></i>
                          {report.ticks_used}
                        </td>
                        <td>{new Date(report.created_at).toLocaleString('ru-RU')}</td>
                        <td>
                          <span className={`report-status ${getStatusClass(report.status)}`}>
                            {getStatusText(report.status)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {report.feedback === 'like' && (
                            <i className="fas fa-thumbs-up" style={{ color: '#27ae60' }} title="Нравится"></i>
                          )}
                          {report.feedback === 'dislike' && (
                            <i className="fas fa-thumbs-down" style={{ color: '#e74c3c' }} title="Не нравится"></i>
                          )}
                          {!report.feedback && (
                            <span style={{ color: '#ccc' }}>—</span>
                          )}
                        </td>
                        <td>
                          {(() => {
                            const rts = reportTokenStats.find(s => s.report_id === report.id);
                            if (!rts || !rts.has_token_data) return <span style={{ color: '#999' }}>—</span>;
                            const isExpanded = expandedTokenReportId === report.id;
                            return (
                              <div>
                                <span
                                  onClick={() => setExpandedTokenReportId(isExpanded ? null : report.id)}
                                  style={{ cursor: 'pointer', borderBottom: '1px dashed #999' }}
                                >
                                  {formatTokenCount(rts.total_tokens)}
                                  {rts.provider && <small style={{ color: '#999', marginLeft: '4px' }}>({rts.provider})</small>}
                                  {rts.cost_usd > 0 && <small style={{ color: '#4CAF50', marginLeft: '4px' }}>{formatCostUSD(rts.cost_usd)}</small>}
                                </span>
                                {isExpanded && (
                                  <div style={{ fontSize: '11px', color: '#666', marginTop: '6px', lineHeight: '1.6' }}>
                                    {rts.breakdown.analytics && rts.breakdown.analytics.total > 0 && (
                                      <div>Аналитика: <strong>{formatTokenCount(rts.breakdown.analytics.total)}</strong>
                                        {rts.breakdown.analytics.provider && <span style={{ color: '#999' }}> ({rts.breakdown.analytics.provider})</span>}
                                        {rts.breakdown.analytics.cost_usd ? <span style={{ color: '#4CAF50' }}> {formatCostUSD(rts.breakdown.analytics.cost_usd)}</span> : null}
                                      </div>
                                    )}
                                    {rts.breakdown.reports && rts.breakdown.reports.length > 0 && rts.breakdown.reports.map((card, i) => (
                                      <div key={i} style={{ paddingLeft: '8px' }}>
                                        {card.name}: <strong>{formatTokenCount(card.total)}</strong>
                                        {card.provider && <span style={{ color: '#999' }}> ({card.provider})</span>}
                                        {card.cost_usd ? <span style={{ color: '#4CAF50' }}> {formatCostUSD(card.cost_usd)}</span> : null}
                                      </div>
                                    ))}
                                    {rts.breakdown.summary && rts.breakdown.summary.total > 0 && (
                                      <div>Итоги: <strong>{formatTokenCount(rts.breakdown.summary.total)}</strong>
                                        {rts.breakdown.summary.provider && <span style={{ color: '#999' }}> ({rts.breakdown.summary.provider})</span>}
                                        {rts.breakdown.summary.cost_usd ? <span style={{ color: '#4CAF50' }}> {formatCostUSD(rts.breakdown.summary.cost_usd)}</span> : null}
                                      </div>
                                    )}
                                    <div style={{ marginTop: '4px', color: '#888', fontSize: '10px' }}>
                                      вход: {rts.input_tokens.toLocaleString()} / выход: {rts.output_tokens.toLocaleString()}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button className="icon-button view" onClick={() => window.open(`/reports/${report.id}`, '_blank')} title="Просмотреть отчет">
                              <i className="fas fa-eye"></i>
                            </button>
                            {report.file_exists && (
                              <button
                                className="icon-button"
                                onClick={() => handleRegenerate(report.id)}
                                disabled={regeneratingReportId === report.id}
                                title="Перегенерировать (бесплатно)"
                                style={{ color: '#2196F3' }}
                              >
                                {regeneratingReportId === report.id
                                  ? <i className="fas fa-spinner fa-spin"></i>
                                  : <i className="fas fa-sync-alt"></i>
                                }
                              </button>
                            )}
                            {report.status === 'completed' && (
                              <button
                                className="icon-button"
                                onClick={() => handleOpenNotifyModal(report.id)}
                                title="Отправить уведомление"
                                style={{ color: '#4CAF50' }}
                              >
                                <i className="fas fa-envelope"></i>
                              </button>
                            )}
                            <button className="icon-button" onClick={() => handleViewJson(report.id)} title="Посмотреть JSON" style={{ color: '#ff9800' }}>
                              <i className="fas fa-code"></i>
                            </button>
                            <button className="icon-button delete" onClick={() => openDeleteConfirmDialog(report.id)} title="Удалить отчет">
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="pagination-container">
                  <div className="pagination-info">
                    Показано {Math.min(itemsPerPage, reports.length - (currentPage - 1) * itemsPerPage)} из {reports.length} отчетов
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-button"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <span className="pagination-current">
                      Страница {currentPage} из {Math.ceil(reports.length / itemsPerPage) || 1}
                    </span>
                    <button
                      className="pagination-button"
                      disabled={currentPage >= Math.ceil(reports.length / itemsPerPage)}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="items-per-page-select"
                    >
                      <option value="5">5 на странице</option>
                      <option value="10">10 на странице</option>
                      <option value="25">25 на странице</option>
                      <option value="50">50 на странице</option>
                    </select>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-reports">
                <p>У пользователя нет отчетов</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        title="Удаление отчета"
        message="Вы уверены, что хотите удалить этот отчет? Это действие нельзя отменить."
        confirmText="Удалить"
        cancelText="Отмена"
        onConfirm={handleDeleteReport}
        onCancel={closeDeleteConfirmDialog}
      />

      {/* JSON viewer modal */}
      {jsonReportId !== null && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => { setJsonReportId(null); setJsonData(null); }}
        >
          <div
            style={{
              background: '#1e1e1e', borderRadius: '8px', width: '80vw', maxHeight: '85vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', borderBottom: '1px solid #333',
            }}>
              <span style={{ color: '#fff', fontWeight: 600 }}>
                JSON отчета #{jsonReportId}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                {jsonData && (
                  <button
                    onClick={() => { navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2)); }}
                    style={{
                      background: '#333', color: '#ccc', border: 'none', borderRadius: '4px',
                      padding: '4px 10px', cursor: 'pointer', fontSize: '12px',
                    }}
                  >
                    <i className="fas fa-copy" style={{ marginRight: '4px' }}></i>Копировать
                  </button>
                )}
                <button
                  onClick={() => { setJsonReportId(null); setJsonData(null); }}
                  style={{
                    background: '#333', color: '#ccc', border: 'none', borderRadius: '4px',
                    padding: '4px 10px', cursor: 'pointer', fontSize: '16px', lineHeight: 1,
                  }}
                >
                  &times;
                </button>
              </div>
            </div>
            <div style={{ overflow: 'auto', padding: '16px', flex: 1 }}>
              {jsonLoading ? (
                <div style={{ color: '#999', textAlign: 'center', padding: '40px' }}>Загрузка...</div>
              ) : (
                <pre style={{
                  color: '#d4d4d4', fontSize: '12px', lineHeight: '1.5',
                  margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace',
                }}>
                  {JSON.stringify(jsonData, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notification modal */}
      {notifyModalReportId !== null && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setNotifyModalReportId(null)}
        >
          <div
            style={{
              background: '#fff', borderRadius: '8px', padding: '24px',
              minWidth: '420px', maxWidth: '500px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '8px' }}>
              Отправить уведомление
            </h3>
            <p style={{ color: '#666', fontSize: '13px', marginBottom: '4px' }}>
              Отчёт: <strong>{notifyReport?.name}</strong> (ID: {notifyModalReportId})
            </p>
            <p style={{ color: '#888', fontSize: '13px', marginBottom: '20px' }}>
              Пользователь получит письмо о том, что генерация заняла больше времени и отчёт бесплатный.
            </p>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>
                ID пользователя (для отправки другому):
              </label>
              <input
                type="number"
                value={notifyTargetUserId}
                onChange={(e) => setNotifyTargetUserId(e.target.value)}
                placeholder={`Владелец: ${notifyReport?.user_id || '—'}`}
                style={{
                  width: '100%', padding: '8px', border: '1px solid #ddd',
                  borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>
                Тестовый email:
              </label>
              <input
                type="email"
                value={notifyTestEmail}
                onChange={(e) => setNotifyTestEmail(e.target.value)}
                placeholder="admin@example.com"
                style={{
                  width: '100%', padding: '8px', border: '1px solid #ddd',
                  borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={() => handleSendNotification('owner')}
                disabled={notifyLoading}
                style={{
                  padding: '10px', background: '#4CAF50', color: '#fff',
                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600,
                  fontSize: '14px', opacity: notifyLoading ? 0.6 : 1,
                }}
              >
                {notifyLoading ? 'Отправка...' : `Отправить владельцу (ID ${notifyReport?.user_id})`}
              </button>

              <button
                onClick={() => handleSendNotification('target')}
                disabled={notifyLoading || !notifyTargetUserId}
                style={{
                  padding: '10px', background: '#7c4dff', color: '#fff',
                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600,
                  fontSize: '14px',
                  opacity: (!notifyTargetUserId || notifyLoading) ? 0.4 : 1,
                }}
              >
                {notifyTargetUserId
                  ? `Отправить пользователю #${notifyTargetUserId}`
                  : 'Отправить другому пользователю'}
              </button>

              <button
                onClick={() => handleSendNotification('test')}
                disabled={notifyLoading || !notifyTestEmail}
                style={{
                  padding: '10px', background: '#2196F3', color: '#fff',
                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600,
                  fontSize: '14px',
                  opacity: (!notifyTestEmail || notifyLoading) ? 0.4 : 1,
                }}
              >
                Тест на email
              </button>

              <button
                onClick={() => setNotifyModalReportId(null)}
                style={{
                  padding: '10px', background: '#f5f5f5', color: '#333',
                  border: 'none', borderRadius: '4px', cursor: 'pointer',
                  fontSize: '14px', marginTop: '4px',
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserReportsPage;
